import { NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivityLocation, Boondock, HelperFunctions, IBoondock, IPhoto, Listing } from '@curbnturf/entities';
import { IListingForm, IPhotoForm } from '@curbnturf/form-helpers';
import { listingComplete } from '@curbnturf/helpers';
import { ImageUploadComponent, ImageUploadOptions } from '@curbnturf/image-processing';
import { CaretakerService, RatingActionTypes, RatingFacade } from '@curbnturf/rating';
import { LoadingController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { DragulaModule } from 'ng2-dragula';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'curbnturf-review-photos',
  templateUrl: './review-photos.component.html',
  styleUrls: ['./review-photos.component.scss'],
  standalone: true,
  imports: [DragulaModule, FormsModule, ImageUploadComponent, NgFor, NgIf, ReactiveFormsModule],
})
export class ReviewPhotosComponent implements OnChanges, OnDestroy {
  @Input() listingForm: FormGroup<IListingForm>;
  @Input() listing?: Listing;
  @Input() boondock?: Boondock;
  @Input() poi?: ActivityLocation;

  @Output() uploaded = new EventEmitter<void>();

  uploadOptions: ImageUploadOptions = {
    output: {
      size: { height: 546, width: 1270 },
    },
    data: {},
  };

  listingComplete = listingComplete;

  getPhotoUrl = HelperFunctions.getPhotoUrlFlatSmall;

  photoList: IPhoto[] = [];

  get photos() {
    if (this.listingForm) {
      return this.listingForm.controls.photos;
    }

    return;
  }

  get listingId(): number | undefined {
    if (this.listing && this.listing.id) {
      return this.listing.id;
    }

    if (this.listingForm) {
      const id = this.listingForm.get('id') as FormControl<number | null>;
      if (id?.value) {
        return id.value;
      }
    }

    return;
  }

  subscriptions: Subscription[] = [];

  constructor(
    private updates$: Actions,
    private caretakerService: CaretakerService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private loadingCtrl: LoadingController,
    private ratingFacade: RatingFacade,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    this.updateSiteId();

    if (changes.listingForm && changes.listingForm.currentValue) {
      const idControl = this.listingForm.get('id') as FormControl<number | null>;
      if (idControl) {
        this.subscriptions.push(idControl.valueChanges.subscribe(() => this.updateSiteId()));
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  async removePhoto(key: string): Promise<void> {
    if (this.listingId) {
      const removeLoader = await this.loadingCtrl.create({
        message: 'Removing Photo...',
      });
      await removeLoader.present();
      this.caretakerService.removePhoto(this.listingId, key);
      const index = this.listingForm.value.photos?.findIndex(
        (photo: { key: string; caption: string }) => photo.key === key,
      );
      if (index !== undefined && index >= 0) {
        (this.listingForm.controls.photos as FormArray<FormGroup<IPhotoForm>>).removeAt(index);
      }

      const boondock: IBoondock = {
        ...this.boondock,
        //...BoondockForm.formatBoondockFormValue(this.listingForm),
      };

      // Make the subscription before sending the request to make sure we don't miss anything.
      this.subscriptions.push(
        this.updates$.pipe(ofType(RatingActionTypes.SelectCaretakerListing)).subscribe(async () => {
          await removeLoader.dismiss();
        }),
      );

      this.ratingFacade.selectCaretakerBoondock({ boondock, boondockId: boondock.id });
    }
  }

  removePhotoFunction(key: string): () => void {
    return () => this.removePhoto(key);
  }

  async save(files: FormData) {
    const load = await this.loadingCtrl?.create({
      message: 'Uploading...',
    });
    await load?.present();

    if (!this.listing) {
      this.listing = new Listing();
    }

    if (this.poi) {
      files.append('poiId', this.poi.id?.toString() || '');

      this.caretakerService.uploadPoiImage(this.poi.id, files).subscribe({
        next: (resp) => {
          this.photoList.push(...resp.photos);

          const photoControls = resp.photos
            .map((photo) =>
              this.fb.group({
                key: [photo?.key ? photo.key : ''],
                caption: [photo?.caption ? photo.caption : '', [Validators.maxLength(120)]],
              }),
            )
            .pop() as FormGroup;

          const formPhotos = this.listingForm.get('photos') as FormArray;
          formPhotos.push(photoControls);

          this.cdr.detectChanges();
        },
        complete: () => {
          this.uploaded.emit();
          load?.dismiss();
        },
      });
    } else if (this.listing) {
      files.append('listingId', String(this.listing?.id) || '');

      this.caretakerService
        .uploadImage(this.listingId as number, files)
        .pipe(
          finalize(() => {
            this.uploaded.emit();
            load?.dismiss();
          }),
        )
        .subscribe((resp) => {
          this.photoList.push(...resp.photos);

          const photoControls = resp.photos
            .map((photo) =>
              this.fb.group({
                key: [photo?.key ? photo.key : ''],
                caption: [photo?.caption ? photo.caption : '', [Validators.maxLength(120)]],
              }),
            )
            .pop() as FormGroup;

          const formPhotos = this.listingForm.get('photos') as FormArray;
          formPhotos.push(photoControls);

          this.cdr.detectChanges();

          this.uploaded.emit();

          load?.dismiss();
        });
    }

    this.listingForm.markAsPristine();
  }

  private updateSiteId() {
    this.uploadOptions.data = {
      listingId: this.listingId,
    };
  }
}
